// 타입 안전한 fetch 클라이언트.
// - 세션(HTTP-only 쿠키) 기반 인증: 모든 요청에 credentials:"include"로 쿠키를 송수신한다.
// - 401 시 /auth/refresh(쿠키)로 토큰 갱신 → 재시도(한 번). 실패하면 만료 콜백.
// - 에러는 백엔드 공통 형태 { code, message }로 정규화
//
// NOTE: 추후 OpenAPI 생성 클라이언트(src/api/generated/)가 이 fetch 래퍼를 사용하도록
// 연결할 예정이다. 인증/리프레시 횡단 관심사는 여기 한 곳에 둔다.

import { env } from '~/env'
import type { ApiErrorBody } from '~/api/types'

/** 백엔드 에러 응답을 감싸는 클라이언트 예외. */
export class ApiError extends Error {
    readonly status: number
    readonly code: string

    constructor(status: number, body: ApiErrorBody) {
        super(body.message)
        this.name = 'ApiError'
        this.status = status
        this.code = body.code
    }
}

/**
 * 인증 상태 훅. auth-store가 구현을 주입해 순환 의존을 피한다.
 * 토큰은 HTTP-only 쿠키에 있어 JS가 접근하지 않으므로, 만료 신호만 받는다.
 */
export interface AuthBridge {
    /** refresh까지 실패해 세션이 만료됐을 때 호출된다(스토어 비우기 등). */
    onAuthExpired(): void
}

let authBridge: AuthBridge | null = null

/** 앱 부팅 시 auth-store가 호출해 브리지를 등록한다. */
export function registerAuthBridge(bridge: AuthBridge): void {
    authBridge = bridge
}

function buildUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) return path
    const base = env.API_BASE_URL.replace(/\/$/, '')
    const suffix = path.startsWith('/') ? path : `/${path}`
    return `${base}${suffix}`
}

async function parseError(res: Response): Promise<ApiError> {
    let body: ApiErrorBody = { code: 'UnknownError', message: res.statusText }
    try {
        const data = (await res.json()) as Partial<ApiErrorBody>
        body = {
            code: data.code ?? 'UnknownError',
            message: data.message ?? res.statusText,
        }
    } catch {
        // JSON이 아니면 statusText로 둔다.
    }
    return new ApiError(res.status, body)
}

// 동시 401이 여러 개 떠도 refresh는 한 번만 수행하도록 공유 Promise로 직렬화한다.
let refreshInFlight: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            try {
                // refresh 토큰은 HTTP-only 쿠키로 전달된다(바디 불필요).
                const res = await fetch(buildUrl('/auth/refresh'), {
                    method: 'POST',
                    credentials: 'include',
                })
                return res.ok
            } catch {
                return false
            } finally {
                refreshInFlight = null
            }
        })()
    }
    return refreshInFlight
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
    /** JSON 직렬화될 바디. 문자열/Blob 등이면 raw로 전달된다. */
    body?: unknown
    /** true면 401 시 refresh 재시도를 하지 않는다(로그인/리프레시 등). */
    skipAuth?: boolean
    /** 내부 재시도 플래그. 직접 설정하지 않는다. */
    _retried?: boolean
}

/**
 * 저수준 fetch. Response를 그대로 반환한다(스트리밍 등에서 사용).
 * 쿠키를 항상 동봉하고, 401이면 refresh 후 한 번 재시도한다.
 */
export async function apiRequest(
    path: string,
    options: ApiFetchOptions = {}
): Promise<Response> {
    const { body, skipAuth, _retried, headers, ...rest } = options

    const computedHeaders = new Headers(headers)

    let payload: BodyInit | undefined
    if (body !== undefined && body !== null) {
        if (
            typeof body === 'string' ||
            body instanceof Blob ||
            body instanceof FormData
        ) {
            payload = body as BodyInit
        } else {
            payload = JSON.stringify(body)
            if (!computedHeaders.has('Content-Type')) {
                computedHeaders.set('Content-Type', 'application/json')
            }
        }
    }

    const res = await fetch(buildUrl(path), {
        ...rest,
        credentials: 'include',
        headers: computedHeaders,
        body: payload,
    })

    if (res.status === 401 && !skipAuth && !_retried) {
        const ok = await refreshSession()
        if (ok) {
            return apiRequest(path, { ...options, _retried: true })
        }
        authBridge?.onAuthExpired()
    }

    return res
}

/** JSON 응답을 파싱해 반환한다. 실패하면 ApiError를 던진다. */
export async function apiFetch<T>(
    path: string,
    options: ApiFetchOptions = {}
): Promise<T> {
    const res = await apiRequest(path, options)
    if (!res.ok) throw await parseError(res)
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
}
